import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, KeyRound } from 'lucide-react';
import { signInWithGoogle, signInWithEmail, resetPassword } from '../services/authService';
import { isUserAllowed } from '../services/adminService';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAllowed } = useAuth();

  const from = location.state?.from?.pathname || '/host';
  const wasDenied = location.state?.denied;

  if (user && isAllowed) {
    navigate(from, { replace: true });
    return null;
  }

  const handleLoginResult = async (firebaseUser) => {
    const allowed = await isUserAllowed(firebaseUser.email);
    if (!allowed) {
      setError('Du har ikkje tilgang. Kontakt administrator for å få tilgang.');
      return;
    }
    navigate(from, { replace: true });
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const user = await signInWithGoogle();
      await handleLoginResult(user);
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user') return;
      setError('Innlogging med Google feila. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Fyll inn e-post og passord');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const user = await signInWithEmail(email.trim(), password);
      await handleLoginResult(user);
    } catch (err) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Feil e-post eller passord');
      } else if (err.code === 'auth/too-many-requests') {
        setError('For mange forsøk. Vent litt og prøv igjen.');
      } else {
        setError('Innlogging feila. Prøv igjen.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email.trim()) {
      setError('Skriv inn e-postadressa di først');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await resetPassword(email.trim());
      setResetSent(true);
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setError('Fann ikkje brukar med denne e-postadressa');
      } else {
        setError('Kunne ikkje sende tilbakestilling. Prøv igjen.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-mp-bg-lightgreen flex flex-col items-center font-body">
      {/* Logo & Title */}
      <div className="mt-12 mb-2 flex flex-col items-center">
        <img
          src="/Mikkel tenker uten bakgrunn.png"
          alt="Mikkel"
          className="w-28 h-28 object-contain mb-2"
        />
        <span className="mp-eyebrow">Mikkel kviss · Vert</span>
      </div>

      {/* Login Card */}
      <div className="w-full max-w-md px-4 mt-6">
        <div className="bg-white rounded-[20px] shadow-mp-2 border border-[rgba(17,73,66,0.12)] p-8">
          <h1 className="font-display text-2xl text-mp-darkgreen text-center mb-2">
            Logg inn
          </h1>
          <p className="text-mp-stone-700 text-center text-sm mb-6">
            Velkomen tilbake! Logg inn for å fortsetje.
          </p>

          {(error || wasDenied) && (
            <div className="bg-[rgba(163,0,97,0.06)] border border-[rgba(163,0,97,0.20)] text-mp-magenta rounded-xl px-4 py-3 mb-4 text-sm">
              {error || 'Du har ikkje tilgang. Kontakt administrator.'}
            </div>
          )}

          {resetSent && (
            <div className="bg-mp-bg-lightgreen border border-[rgba(17,73,66,0.12)] text-mp-darkgreen rounded-xl px-4 py-3 mb-4 text-sm">
              E-post med tilbakestilling av passord er sendt til {email}
            </div>
          )}

          {/* Google Login */}
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 border border-[rgba(17,73,66,0.20)] rounded-full py-3.5 px-4 text-mp-darkgreen font-display font-bold hover:bg-mp-bg-lightgreen transition-colors disabled:opacity-50"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Logg inn med Google
          </button>

          {/* Divider */}
          <div className="flex items-center my-6">
            <div className="flex-1 border-t border-[rgba(17,73,66,0.12)]" />
            <span className="px-4 text-mp-teal text-[11px] font-display font-bold uppercase tracking-[0.14em]">Eller</span>
            <div className="flex-1 border-t border-[rgba(17,73,66,0.12)]" />
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-display font-bold text-mp-darkgreen mb-1.5">
                E-post
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="din@epost.no"
                className="w-full border border-[rgba(17,73,66,0.20)] rounded-xl px-4 py-3 text-mp-darkgreen placeholder-mp-stone-500 focus:outline-none focus:ring-3 focus:ring-[rgba(0,137,134,0.18)] focus:border-mp-teal transition-colors"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-display font-bold text-mp-darkgreen">
                  Passord
                </label>
                <button
                  type="button"
                  onClick={handleResetPassword}
                  className="text-mp-magenta text-sm font-display font-bold hover:underline inline-flex items-center gap-1"
                >
                  <KeyRound size={14} />
                  Gløymt passord?
                </button>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full border border-[rgba(17,73,66,0.20)] rounded-xl px-4 py-3 text-mp-darkgreen placeholder-mp-stone-500 focus:outline-none focus:ring-3 focus:ring-[rgba(0,137,134,0.18)] focus:border-mp-teal transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mp-cta mp-cta-large w-full"
            >
              {loading ? 'Loggar inn…' : 'Logg inn'}
            </button>
          </form>
        </div>
      </div>

      {/* Back to home link */}
      <Link
        to="/"
        className="mt-6 mb-8 text-mp-magenta font-display font-bold text-sm hover:underline inline-flex items-center gap-1"
      >
        <ArrowLeft size={16} /> Tilbake til framsida
      </Link>

      <footer className="mt-auto p-6 text-center">
        <p className="text-mp-stone-500 text-[12px]">© 2026 Mikkelparken AS</p>
      </footer>
    </div>
  );
}
